/**
 * The server's timezone offset rules (SCRUM-2428).
 *
 * The API sends `tzRules` with the staff user: every UTC-offset change, from
 * PHP's timezone database, of the zones this session shows times in (the
 * user's, the account's, its facilities' and Pacific). Times convert with
 * these instead of the browser's own timezone copy, which only knows the rules
 * it shipped with. A browser built before BC stopped changing clocks on
 * 2026-11-01 still puts Vancouver at UTC-8 in winter and shows every later
 * time an hour early, while a current one is right; with the server's rules
 * every machine shows the same clock, the one the API saves with.
 *
 * The lookups answer null for a zone or instant outside the table, and callers
 * then use the browser as before: anonymous pages, a zone from another
 * account, dates far in the past (a stale copy still has the past right).
 */

/** `[startsAt (unix seconds), offsetMinutes, abbreviation]`, in time order. */
export type TzRuleRow = [number, number, string];

export interface TzRules {
  /** Unix seconds; the first row of every zone starts here. */
  from: number;
  /** Unix seconds; instants from here on are left to the browser. */
  until: number;
  zones: Record<string, TzRuleRow[]>;
}

let current: TzRules | null = null;
let lastInput: unknown = null;

export function isTzRules(value: unknown): value is TzRules {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.from === 'number' &&
    typeof v.until === 'number' &&
    !!v.zones &&
    typeof v.zones === 'object' &&
    !Array.isArray(v.zones)
  );
}

/** Install the rules the lookups read; anything malformed clears them. */
export function setTzRules(rules: unknown): void {
  if (rules === lastInput) return;
  lastInput = rules;
  current = isTzRules(rules) ? rules : null;
}

function rowAt(tz: string, utcMs: number): TzRuleRow | null {
  if (!current || !Number.isFinite(utcMs)) return null;
  const rows = current.zones[tz];
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const seconds = Math.floor(utcMs / 1000);
  if (seconds < current.from || seconds >= current.until || rows[0][0] > seconds) return null;

  // Last row starting at or before `seconds`.
  let lo = 0;
  let hi = rows.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (rows[mid][0] <= seconds) lo = mid;
    else hi = mid - 1;
  }
  return rows[lo];
}

/** Minutes east of UTC in `tz` at `utcMs` (Vancouver in winter 2026: -420), or null. */
export function serverOffsetMinutes(tz: string, utcMs: number): number | null {
  return rowAt(tz, utcMs)?.[1] ?? null;
}

/** The zone's abbreviation at `utcMs` per the server ("PDT", "MST"), or null. */
export function serverZoneAbbreviation(tz: string, utcMs: number): string | null {
  return rowAt(tz, utcMs)?.[2] ?? null;
}

/**
 * Whose rules a payload means the page converts with: the server's, or the
 * browser's while none have arrived. Layout keys the page on this, so a page
 * that rendered before the rules arrived (the first load of a session signed
 * in before they existed) renders again once with them. It deliberately
 * ignores the rules' content: a timezone picked in the user menu brings new
 * rules too, and remounting then would throw away unsaved edits on the page.
 * That change also changes the zone name, which re-renders the times anyway.
 */
export function tzRulesSource(rules: unknown): 'server' | 'browser' {
  return isTzRules(rules) ? 'server' : 'browser';
}
